import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { Check, Send, TrendingUp, BarChart3, Users, LogIn, LogOut, MessageCircle, Clock, BookOpen, LineChart, Target } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group";
import { Label } from "./components/ui/label";
import { TickerTape, SymbolOverview } from "./components/TradingViewWidgets";
import { supabase } from "./lib/supabase";
import { Toaster, toast } from "sonner";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_IMAGE = "https://images.unsplash.com/photo-1639825752750-5061ded5503b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc3RvY2slMjBtYXJrZXQlMjB0cmFkaW5nJTIwY2hhcnQlMjBpbnRlcmZhY2UlMjBzY3JlZW4lMjBkYXJrJTIwbW9kZXxlbnwwfHx8fDE3NzMyNjE4MjJ8MA&ixlib=rb-4.1.0&q=85";

const TELEGRAM_URL = "https://t.me/alvo13_official";
const WHATSAPP_NUMBER = "351911770730";
const WHATSAPP_BASE_URL = "https://wa.me/351911770730?text=Hello!%20I%20want%20to%20join%20ALVO%2013";

const getPackageWhatsAppURL = (packageName, price) => {
  const message = encodeURIComponent(`Hello! I want to join ALVO 13. Package: ${packageName} (${price}€)`);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
};

const PACKAGE_IDS = ["technical_analysis", "investment_base", "full_mentor"];

// Translations
const translations = {
  EN: {
    badge: "Est. 2012 • 13 Years of Excellence",
    heroTitle: "ALVO 13: 13 YEARS OF TRADING EXPERTISE",
    heroSubtitle: "The Mathematics of Goals. The Justice of Results.",
    ctaButton: "Start Your Strategy Quiz",
    chartTitle: "TradingView • BTC/USDT",
    pricingBadge: "Investment Plans",
    pricingTitle: "Choose Your Path",
    contactBadge: "Ready to Begin?",
    contactTitle: "Follow Us",
    contactDesc: "Follow our market news and analytics:",
    footer: "All rights reserved.",
    buyNow: "Buy Now",
    academy: {
      badge: "Free Resources",
      title: "ALVO 13 Academy",
      getMaterial: "Get Material",
      books: [
        { name: "Trading Fundamentals", desc: "Master the basics of market analysis and trading psychology" },
        { name: "Technical Analysis Guide", desc: "Learn chart patterns, indicators, and price action strategies" },
        { name: "Risk Management Manual", desc: "Protect your capital with proven risk management techniques" }
      ]
    },
    getStarted: "Get Started",
    login: "Log in",
    logout: "Log out",
    loginTitle: "Welcome to ALVO 13",
    loginSubtitle: "Sign in to access your client portal",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    signIn: "Sign In",
    signUp: "Sign Up",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    continueWithGoogle: "Continue with Google",
    orContinueWith: "or continue with",
    orContactUs: "Or contact us directly",
    authError: "Invalid email or password",
    signupSuccess: "Check your email to confirm your account",
    comingSoon: "Coming Soon",
    comingSoonDesc: "Client portal is under development. Stay tuned!",
    popular: "POPULAR",
    tiers: [
      {
        name: "Technical Analysis",
        features: ["Basic Patterns", "Market Structure", "Risk Management", "Personal Mentorship"],
      },
      {
        name: "Investment Base",
        features: ["Portfolio Building", "Long-term Strategy", "Fundamental Analysis", "Personal Mentorship"],
      },
      {
        name: "Full Mentor",
        features: ["Includes everything from previous packages", "1-on-1 Mentorship", "Live Trading", "Custom Strategy", "Personal Mentorship"],
      },
    ],
    quiz: {
      title: "Strategy Quiz",
      subtitle: "Answer 6 questions to find your perfect plan",
      q1: "How long have you been trading or studying the markets?",
      q1_a1: "Beginner",
      q1_a2: "1-2 years",
      q1_a3: "2+ years",
      q2: "Which markets are you most interested in?",
      q2_a1: "Crypto",
      q2_a2: "Stocks",
      q2_a3: "Gold & Forex",
      q3: "What is your estimated trading capital?",
      q3_a1: "Under 1000€",
      q3_a2: "1000€ - 5000€",
      q3_a3: "5000€+",
      q4: "How much time can you dedicate to trading per day?",
      q4_a1: "Less than 1 hour",
      q4_a2: "1-3 hours",
      q4_a3: "Full-time",
      q5: "How do you handle a market drawdown of 10%?",
      q5_a1: "Panic",
      q5_a2: "Hold",
      q5_a3: "Buy more",
      q6: "What is your primary goal with ALVO 13?",
      q6_a1: "Passive income",
      q6_a2: "Professional career",
      q6_a3: "Learn the math of trading",
      next: "Next",
      back: "Back",
      submit: "See My Recommendation",
      result: "Based on your answers, we recommend:",
      contactUs: "Get My Plan",
      whatsappMsg: "Hello! I completed the ALVO 13 quiz.",
    },
  },
  RU: {
    badge: "С 2012 года • 13 лет мастерства",
    heroTitle: "ALVO 13: 13 ЛЕТ ТОРГОВОГО ОПЫТА",
    heroSubtitle: "Математика цели. Справедливость результата.",
    ctaButton: "Начать Стратегический Тест",
    chartTitle: "TradingView • BTC/USDT",
    pricingBadge: "Инвестиционные Планы",
    pricingTitle: "Выберите Свой Путь",
    contactBadge: "Готовы Начать?",
    contactTitle: "Следите за нами",
    contactDesc: "Следите за нашими новостями и аналитикой рынка:",
    footer: "Все права защищены.",
    buyNow: "Купить",
    academy: {
      badge: "Бесплатные Материалы",
      title: "Академия ALVO 13",
      getMaterial: "Получить Материал",
      books: [
        { name: "Основы Трейдинга", desc: "Освойте базовые принципы анализа рынка и психологии торговли" },
        { name: "Руководство по Техническому Анализу", desc: "Изучите паттерны, индикаторы и стратегии ценового движения" },
        { name: "Управление Рисками", desc: "Защитите свой капитал с помощью проверенных техник управления рисками" }
      ]
    },
    getStarted: "Начать",
    login: "Вход",
    logout: "Выход",
    loginTitle: "Добро пожаловать в ALVO 13",
    loginSubtitle: "Войдите для доступа к клиентскому порталу",
    emailPlaceholder: "Электронная почта",
    passwordPlaceholder: "Пароль",
    signIn: "Войти",
    signUp: "Регистрация",
    noAccount: "Нет аккаунта?",
    hasAccount: "Уже есть аккаунт?",
    continueWithGoogle: "Войти через Google",
    orContinueWith: "или продолжить с",
    orContactUs: "Или свяжитесь с нами напрямую",
    authError: "Неверный email или пароль",
    signupSuccess: "Проверьте почту для подтверждения аккаунта",
    comingSoon: "Скоро",
    comingSoonDesc: "Клиентский портал в разработке. Следите за обновлениями!",
    popular: "ПОПУЛЯРНЫЙ",
    tiers: [
      {
        name: "Технический Анализ",
        features: ["Базовые Паттерны", "Структура Рынка", "Управление Рисками", "Личное Наставничество"],
      },
      {
        name: "Инвестиционная База",
        features: ["Построение Портфеля", "Долгосрочная Стратегия", "Фундаментальный Анализ", "Личное Наставничество"],
      },
      {
        name: "Полный Менторинг",
        features: ["Включает всё из предыдущих пакетов", "Личное Наставничество", "Живой Трейдинг", "Индивидуальная Стратегия"],
      },
    ],
    quiz: {
      title: "Стратегический Тест",
      subtitle: "Ответьте на 6 вопросов, чтобы найти идеальный план",
      q1: "Как долго вы торгуете или изучаете рынки?",
      q1_a1: "Новичок",
      q1_a2: "1-2 года",
      q1_a3: "2+ года",
      q2: "Какие рынки вас больше всего интересуют?",
      q2_a1: "Криптовалюты",
      q2_a2: "Акции",
      q2_a3: "Золото и Форекс",
      q3: "Каков ваш примерный торговый капитал?",
      q3_a1: "Менее 1000€",
      q3_a2: "1000€ - 5000€",
      q3_a3: "5000€+",
      q4: "Сколько времени в день вы можете уделять торговле?",
      q4_a1: "Менее 1 часа",
      q4_a2: "1-3 часа",
      q4_a3: "Полный день",
      q5: "Как вы реагируете на просадку рынка в 10%?",
      q5_a1: "Паника",
      q5_a2: "Держу",
      q5_a3: "Докупаю",
      q6: "Какова ваша главная цель с ALVO 13?",
      q6_a1: "Пассивный доход",
      q6_a2: "Профессиональная карьера",
      q6_a3: "Изучить математику трейдинга",
      next: "Далее",
      back: "Назад",
      submit: "Показать Рекомендацию",
      result: "На основе ваших ответов мы рекомендуем:",
      contactUs: "Получить план",
      whatsappMsg: "Привет! Я прошел тест ALVO 13.",
    },
  },
  ES: {
    badge: "Desde 2012 • 13 Años de Excelencia",
    heroTitle: "ALVO 13: 13 AÑOS DE EXPERIENCIA EN TRADING",
    heroSubtitle: "Las Matemáticas del Objetivo. La Justicia del Resultado.",
    ctaButton: "Iniciar Test de Estrategia",
    chartTitle: "TradingView • BTC/USDT",
    pricingBadge: "Planes de Inversión",
    pricingTitle: "Elige Tu Camino",
    contactBadge: "¿Listo para Comenzar?",
    contactTitle: "Síguenos",
    contactDesc: "Siga nuestras noticias y análisis de mercado:",
    footer: "Todos los derechos reservados.",
    buyNow: "Comprar",
    academy: {
      badge: "Recursos Gratuitos",
      title: "Academia ALVO 13",
      getMaterial: "Obtener Material",
      books: [
        { name: "Fundamentos del Trading", desc: "Domina los conceptos básicos del análisis de mercado y la psicología del trading" },
        { name: "Guía de Análisis Técnico", desc: "Aprende patrones de gráficos, indicadores y estrategias de acción del precio" },
        { name: "Manual de Gestión de Riesgos", desc: "Protege tu capital con técnicas probadas de gestión de riesgos" }
      ]
    },
    getStarted: "Comenzar",
    login: "Entrar",
    logout: "Salir",
    loginTitle: "Bienvenido a ALVO 13",
    loginSubtitle: "Inicia sesión para acceder al portal del cliente",
    emailPlaceholder: "Correo electrónico",
    passwordPlaceholder: "Contraseña",
    signIn: "Iniciar Sesión",
    signUp: "Registrarse",
    noAccount: "¿No tienes cuenta?",
    hasAccount: "¿Ya tienes cuenta?",
    continueWithGoogle: "Continuar con Google",
    orContinueWith: "o continuar con",
    orContactUs: "O contáctenos directamente",
    authError: "Email o contraseña inválidos",
    signupSuccess: "Revisa tu email para confirmar tu cuenta",
    comingSoon: "Próximamente",
    comingSoonDesc: "El portal del cliente está en desarrollo. ¡Manténgase atento!",
    popular: "POPULAR",
    tiers: [
      {
        name: "Análisis Técnico",
        features: ["Patrones Básicos", "Estructura del Mercado", "Gestión de Riesgos", "Mentoría Personal"],
      },
      {
        name: "Base de Inversión",
        features: ["Construcción de Cartera", "Estrategia a Largo Plazo", "Análisis Fundamental", "Mentoría Personal"],
      },
      {
        name: "Mentor Completo",
        features: ["Incluye todo de los paquetes anteriores", "Mentoría Personal", "Trading en Vivo", "Estrategia Personalizada"],
      },
    ],
    quiz: {
      title: "Test de Estrategia",
      subtitle: "Responde 6 preguntas para encontrar tu plan perfecto",
      q1: "¿Cuánto tiempo llevas operando o estudiando los mercados?",
      q1_a1: "Principiante",
      q1_a2: "1-2 años",
      q1_a3: "2+ años",
      q2: "¿Qué mercados te interesan más?",
      q2_a1: "Cripto",
      q2_a2: "Acciones",
      q2_a3: "Oro y Forex",
      q3: "¿Cuál es tu capital estimado de trading?",
      q3_a1: "Menos de 1000€",
      q3_a2: "1000€ - 5000€",
      q3_a3: "5000€+",
      q4: "¿Cuánto tiempo puedes dedicar al trading por día?",
      q4_a1: "Menos de 1 hora",
      q4_a2: "1-3 horas",
      q4_a3: "Tiempo completo",
      q5: "¿Cómo manejas una caída del mercado del 10%?",
      q5_a1: "Pánico",
      q5_a2: "Mantengo",
      q5_a3: "Compro más",
      q6: "¿Cuál es tu objetivo principal con ALVO 13?",
      q6_a1: "Ingresos pasivos",
      q6_a2: "Carrera profesional",
      q6_a3: "Aprender las matemáticas del trading",
      next: "Siguiente",
      back: "Atrás",
      submit: "Ver Mi Recomendación",
      result: "Según tus respuestas, te recomendamos:",
      contactUs: "Obtener mi plan",
      whatsappMsg: "¡Hola! Completé el quiz de ALVO 13.",
    },
  },
};

const prices = ["350", "600", "900"];
const icons = [TrendingUp, BarChart3, Users];

function App() {
  const [activeLang, setActiveLang] = useState("EN");
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizStep, setQuizStep] = useState(1);
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "" });
  const [showResult, setShowResult] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authMode, setAuthMode] = useState("signin");
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");

  const t = translations[activeLang];

  // Supabase auth listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s) syncUserProfile(s.access_token);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s) syncUserProfile(s.access_token);
    });

    return () => subscription.unsubscribe();
  }, []);

  const syncUserProfile = async (token) => {
    try {
      await fetch(`${API_URL}/api/auth/callback`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // silent
    }
  };

  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    setAuthLoading(false);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    setAuthLoading(true);
    try {
      if (authMode === "signup") {
        const { error } = await supabase.auth.signUp({ email: authEmail, password: authPassword });
        if (error) { setAuthError(error.message); }
        else { setAuthSuccess(t.signupSuccess); }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
        if (error) { setAuthError(t.authError); }
      }
    } catch (err) {
      setAuthError(t.authError);
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  const handleBuyNow = async (packageIndex) => {
    if (!session) {
      setLoginOpen(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          package_id: PACKAGE_IDS[packageIndex],
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      // silent
    }
  };

  // Poll payment status on return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentStatus = params.get("payment");
    if (paymentStatus === "success" && sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
          const data = await res.json();
          if (data.payment_status === "paid") {
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
        } catch (e) {
          // silent
        }
        setTimeout(() => pollStatus(attempts + 1), 2000);
      };
      pollStatus();
    } else if (paymentStatus === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Referral detection — reads ref saved by inline script in index.html
  useEffect(() => {
    try {
      const refCode = localStorage.getItem("alvo13_ref");
      const refTs = localStorage.getItem("alvo13_ref_ts");
      if (refCode && refTs) {
        const age = Date.now() - parseInt(refTs, 10);
        // Show toast only if captured in the last 10 seconds (fresh landing)
        if (age < 10000) {
          toast.success(`Referral code "${refCode}" successfully applied!`, { duration: 5000 });
        }
        // Clear the timestamp so toast doesn't re-fire on navigation
        localStorage.removeItem("alvo13_ref_ts");
      }
    } catch (e) { /* localStorage blocked */ }
  }, []);

  const trackReferralAction = (action) => {
    const refCode = localStorage.getItem("alvo13_ref");
    if (refCode) {
      toast.success(`Lead tracked! Referrer: ${refCode} | Action: ${action}`, { duration: 4000 });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openQuiz = () => {
    setQuizStep(1);
    setAnswers({ q1: "", q2: "", q3: "", q4: "", q5: "", q6: "" });
    setShowResult(false);
    setQuizOpen(true);
  };

  const getRecommendation = () => {
    // Score based on all 6 answers
    let score = 0;
    
    // Q1: Experience
    if (answers.q1 === "2+years") score += 3;
    else if (answers.q1 === "1-2years") score += 2;
    else score += 1;
    
    // Q3: Capital
    if (answers.q3 === "5000+") score += 3;
    else if (answers.q3 === "1000-5000") score += 2;
    else score += 1;
    
    // Q4: Time
    if (answers.q4 === "fulltime") score += 3;
    else if (answers.q4 === "1-3hours") score += 2;
    else score += 1;
    
    // Q5: Psychology
    if (answers.q5 === "buymore") score += 3;
    else if (answers.q5 === "hold") score += 2;
    else score += 1;
    
    // Q6: Goal
    if (answers.q6 === "career") score += 3;
    else if (answers.q6 === "math") score += 2;
    else score += 1;
    
    if (score >= 12) return { tier: t.tiers[2].name, price: "900€" };
    if (score >= 8) return { tier: t.tiers[1].name, price: "600€" };
    return { tier: t.tiers[0].name, price: "350€" };
  };

  const getAnswerLabels = () => {
    const labels = {
      q1: answers.q1 === "2+years" ? t.quiz.q1_a3 : answers.q1 === "1-2years" ? t.quiz.q1_a2 : t.quiz.q1_a1,
      q2: answers.q2 === "stocks" ? t.quiz.q2_a2 : answers.q2 === "goldforex" ? t.quiz.q2_a3 : t.quiz.q2_a1,
      q3: answers.q3 === "5000+" ? t.quiz.q3_a3 : answers.q3 === "1000-5000" ? t.quiz.q3_a2 : t.quiz.q3_a1,
      q4: answers.q4 === "fulltime" ? t.quiz.q4_a3 : answers.q4 === "1-3hours" ? t.quiz.q4_a2 : t.quiz.q4_a1,
      q5: answers.q5 === "buymore" ? t.quiz.q5_a3 : answers.q5 === "hold" ? t.quiz.q5_a2 : t.quiz.q5_a1,
      q6: answers.q6 === "career" ? t.quiz.q6_a3 : answers.q6 === "math" ? t.quiz.q6_a2 : t.quiz.q6_a1,
    };
    return labels;
  };

  const handleQuizSubmit = () => {
    setShowResult(true);
    trackReferralAction("Quiz Completed");
  };

  const canProceed = () => {
    if (quizStep === 1) return answers.q1 !== "";
    if (quizStep === 2) return answers.q2 !== "";
    if (quizStep === 3) return answers.q3 !== "";
    if (quizStep === 4) return answers.q4 !== "";
    if (quizStep === 5) return answers.q5 !== "";
    if (quizStep === 6) return answers.q6 !== "";
    return false;
  };

  // If logged in, show Dashboard
  if (session) {
    return (
      <Dashboard
        session={session}
        onLogout={handleLogout}
        translations={translations}
        activeLang={activeLang}
        setActiveLang={setActiveLang}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F7]">
      <Toaster position="top-right" theme="dark" richColors />
      <nav className="nav-floating" data-testid="navigation">
        <div className="container-custom nav-inner">
          <div className="logo-text" data-testid="logo">ALVO 13</div>
          <div className="flex items-center gap-4">
            <div className="lang-toggle" data-testid="language-toggle">
              {["RU", "EN", "ES"].map((lang) => (
                <button
                  key={lang}
                  className={`lang-btn ${activeLang === lang ? "active" : ""}`}
                  onClick={() => setActiveLang(lang)}
                  data-testid={`lang-btn-${lang.toLowerCase()}`}
                >
                  {lang}
                </button>
              ))}
            </div>
            {session ? (
              <button
                className="login-btn"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>{t.logout}</span>
              </button>
            ) : (
              <button
                className="login-btn"
                onClick={() => setLoginOpen(true)}
                data-testid="login-button"
              >
                <LogIn className="w-4 h-4" />
                <span>{t.login}</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Live Ticker */}
      <TickerTape />

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-badge animate-fade-in">
          <span>{t.badge}</span>
        </div>
        <h1 className="hero-title gradient-text animate-slide-up stagger-1" data-testid="hero-title">
          {t.heroTitle}
        </h1>
        <p className="hero-subtitle animate-slide-up stagger-2" data-testid="hero-subtitle">
          {t.heroSubtitle}
        </p>
        <button
          className="btn-primary animate-slide-up stagger-3"
          onClick={openQuiz}
          data-testid="cta-button-hero"
        >
          {t.ctaButton}
        </button>
      </section>

      {/* Chart Section */}
      <section className="chart-section" data-testid="chart-section">
        <div className="container-custom">
          <div className="chart-wrapper" data-testid="chart-placeholder">
            <div className="chart-header">
              <div className="chart-dots">
                <span className="chart-dot red"></span>
                <span className="chart-dot yellow"></span>
                <span className="chart-dot green"></span>
              </div>
              <span className="chart-title-premium">MARKET WATCH</span>
            </div>
            <div className="chart-widget-container">
              <SymbolOverview />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" data-testid="pricing-section">
        <div className="container-custom">
          <div className="section-header">
            <span className="section-badge">{t.pricingBadge}</span>
            <h2 className="section-title gradient-text" data-testid="pricing-title">{t.pricingTitle}</h2>
          </div>
          <div className="pricing-grid">
            {t.tiers.map((tier, index) => {
              const IconComponent = icons[index];
              const isFeatured = index === 1;
              return (
                <div
                  key={index}
                  className={`pricing-card ${isFeatured ? "featured" : ""}`}
                  data-testid={`pricing-card-${index}`}
                >
                  {isFeatured && <span className="popular-badge">{t.popular}</span>}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-white/5">
                      <IconComponent className="w-5 h-5 text-[#A1A1AA]" />
                    </div>
                    <span className="pricing-name">{tier.name}</span>
                  </div>
                  <div className="pricing-price" data-testid={`price-${index}`}>
                    {prices[index]}<span className="currency">€</span>
                  </div>
                  <ul className="pricing-features">
                    {tier.features.map((feature, fIndex) => (
                      <li key={fIndex}>
                        <Check className="check-icon" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleBuyNow(index)}
                    className={`buy-btn ${isFeatured ? "featured" : ""}`}
                    data-testid={`buy-btn-${index}`}
                  >
                    {t.buyNow}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section" data-testid="contact-section">
        <div className="container-custom contact-content">
          <span className="section-badge">{t.contactBadge}</span>
          <h2 className="section-title gradient-text" data-testid="contact-title">{t.contactTitle}</h2>
          <p className="hero-subtitle mx-auto" style={{ marginTop: "1rem" }}>
            {t.contactDesc}
          </p>
          <div className="social-icons">
            <a
              href="https://t.me/alvo13_official"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              data-testid="telegram-icon"
              title="Telegram"
            >
              <Send className="w-6 h-6" />
            </a>
            <a
              href="https://www.tradingview.com/u/ALVO13_official/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-link"
              data-testid="tradingview-icon"
              title="TradingView"
            >
              <TrendingUp className="w-6 h-6" />
            </a>
          </div>
        </div>
      </section>

      {/* Academy Section */}
      <section className="academy-section" data-testid="academy-section">
        <div className="container-custom">
          <div className="section-header">
            <span className="section-badge">{t.academy.badge}</span>
            <h2 className="section-title gradient-text" data-testid="academy-title">{t.academy.title}</h2>
          </div>
          <div className="academy-grid">
            {t.academy.books.map((book, index) => {
              const icons = [BookOpen, LineChart, Target];
              const IconComponent = icons[index];
              return (
                <div key={index} className="academy-card" data-testid={`academy-card-${index}`}>
                  <div className="academy-card-icon">
                    <IconComponent className="w-8 h-8" />
                  </div>
                  <h3 className="academy-card-title">{book.name}</h3>
                  <p className="academy-card-desc">{book.desc}</p>
                  <a
                    href={`https://wa.me/351911770730?text=${encodeURIComponent(`Hello! I want to receive the ${book.name} from ALVO 13 Academy.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="academy-btn"
                    data-testid={`academy-btn-${index}`}
                  >
                    {t.academy.getMaterial}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container-custom">
          <p className="footer-text">
            © 2026 ALVO 13. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={WHATSAPP_BASE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="whatsapp-float-btn"
        onClick={() => trackReferralAction("WhatsApp Float")}
      >
        <MessageCircle className="w-6 h-6" />
      </a>

      {/* Quiz Modal */}
      <Dialog open={quizOpen} onOpenChange={setQuizOpen}>
        <DialogContent className="quiz-modal" data-testid="quiz-modal">
          <DialogHeader>
            <DialogTitle className="quiz-modal-title">{t.quiz.title}</DialogTitle>
            <p className="quiz-modal-subtitle">{t.quiz.subtitle}</p>
          </DialogHeader>
          
          {!showResult ? (
            <div className="quiz-content">
              {/* Progress - 6 steps */}
              <div className="quiz-progress">
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <div 
                    key={step} 
                    className={`quiz-progress-dot ${quizStep >= step ? "active" : ""}`}
                  />
                ))}
              </div>

              {/* Question 1 - Experience */}
              {quizStep === 1 && (
                <div className="quiz-question" data-testid="quiz-q1">
                  <h3 className="quiz-question-title">{t.quiz.q1}</h3>
                  <RadioGroup
                    value={answers.q1}
                    onValueChange={(value) => setAnswers({ ...answers, q1: value })}
                    className="quiz-options"
                  >
                    <div className="quiz-option">
                      <RadioGroupItem value="beginner" id="q1-a1" />
                      <Label htmlFor="q1-a1">{t.quiz.q1_a1}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="1-2years" id="q1-a2" />
                      <Label htmlFor="q1-a2">{t.quiz.q1_a2}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="2+years" id="q1-a3" />
                      <Label htmlFor="q1-a3">{t.quiz.q1_a3}</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Question 2 - Markets */}
              {quizStep === 2 && (
                <div className="quiz-question" data-testid="quiz-q2">
                  <h3 className="quiz-question-title">{t.quiz.q2}</h3>
                  <RadioGroup
                    value={answers.q2}
                    onValueChange={(value) => setAnswers({ ...answers, q2: value })}
                    className="quiz-options"
                  >
                    <div className="quiz-option">
                      <RadioGroupItem value="crypto" id="q2-a1" />
                      <Label htmlFor="q2-a1">{t.quiz.q2_a1}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="stocks" id="q2-a2" />
                      <Label htmlFor="q2-a2">{t.quiz.q2_a2}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="goldforex" id="q2-a3" />
                      <Label htmlFor="q2-a3">{t.quiz.q2_a3}</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Question 3 - Capital */}
              {quizStep === 3 && (
                <div className="quiz-question" data-testid="quiz-q3">
                  <h3 className="quiz-question-title">{t.quiz.q3}</h3>
                  <RadioGroup
                    value={answers.q3}
                    onValueChange={(value) => setAnswers({ ...answers, q3: value })}
                    className="quiz-options"
                  >
                    <div className="quiz-option">
                      <RadioGroupItem value="under1000" id="q3-a1" />
                      <Label htmlFor="q3-a1">{t.quiz.q3_a1}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="1000-5000" id="q3-a2" />
                      <Label htmlFor="q3-a2">{t.quiz.q3_a2}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="5000+" id="q3-a3" />
                      <Label htmlFor="q3-a3">{t.quiz.q3_a3}</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Question 4 - Time */}
              {quizStep === 4 && (
                <div className="quiz-question" data-testid="quiz-q4">
                  <h3 className="quiz-question-title">{t.quiz.q4}</h3>
                  <RadioGroup
                    value={answers.q4}
                    onValueChange={(value) => setAnswers({ ...answers, q4: value })}
                    className="quiz-options"
                  >
                    <div className="quiz-option">
                      <RadioGroupItem value="less1hour" id="q4-a1" />
                      <Label htmlFor="q4-a1">{t.quiz.q4_a1}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="1-3hours" id="q4-a2" />
                      <Label htmlFor="q4-a2">{t.quiz.q4_a2}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="fulltime" id="q4-a3" />
                      <Label htmlFor="q4-a3">{t.quiz.q4_a3}</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Question 5 - Psychology */}
              {quizStep === 5 && (
                <div className="quiz-question" data-testid="quiz-q5">
                  <h3 className="quiz-question-title">{t.quiz.q5}</h3>
                  <RadioGroup
                    value={answers.q5}
                    onValueChange={(value) => setAnswers({ ...answers, q5: value })}
                    className="quiz-options"
                  >
                    <div className="quiz-option">
                      <RadioGroupItem value="panic" id="q5-a1" />
                      <Label htmlFor="q5-a1">{t.quiz.q5_a1}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="hold" id="q5-a2" />
                      <Label htmlFor="q5-a2">{t.quiz.q5_a2}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="buymore" id="q5-a3" />
                      <Label htmlFor="q5-a3">{t.quiz.q5_a3}</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Question 6 - Goal */}
              {quizStep === 6 && (
                <div className="quiz-question" data-testid="quiz-q6">
                  <h3 className="quiz-question-title">{t.quiz.q6}</h3>
                  <RadioGroup
                    value={answers.q6}
                    onValueChange={(value) => setAnswers({ ...answers, q6: value })}
                    className="quiz-options"
                  >
                    <div className="quiz-option">
                      <RadioGroupItem value="passive" id="q6-a1" />
                      <Label htmlFor="q6-a1">{t.quiz.q6_a1}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="career" id="q6-a2" />
                      <Label htmlFor="q6-a2">{t.quiz.q6_a2}</Label>
                    </div>
                    <div className="quiz-option">
                      <RadioGroupItem value="math" id="q6-a3" />
                      <Label htmlFor="q6-a3">{t.quiz.q6_a3}</Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Navigation */}
              <div className="quiz-nav">
                {quizStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => setQuizStep(quizStep - 1)}
                    className="quiz-btn-back"
                    data-testid="quiz-back-btn"
                  >
                    {t.quiz.back}
                  </Button>
                )}
                {quizStep < 6 ? (
                  <Button
                    onClick={() => setQuizStep(quizStep + 1)}
                    disabled={!canProceed()}
                    className="quiz-btn-next"
                    data-testid="quiz-next-btn"
                  >
                    {t.quiz.next}
                  </Button>
                ) : (
                  <Button
                    onClick={handleQuizSubmit}
                    disabled={!canProceed()}
                    className="quiz-btn-submit"
                    data-testid="quiz-submit-btn"
                  >
                    {t.quiz.submit}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="quiz-result" data-testid="quiz-result">
              <div className="quiz-result-content">
                <p className="quiz-result-label">{t.quiz.result}</p>
                <div className="quiz-result-recommendation">
                  <span className="quiz-result-tier">{getRecommendation().tier}</span>
                  <span className="quiz-result-price">{getRecommendation().price}</span>
                </div>
              </div>
              <a
                href={`https://wa.me/351911770730?text=${encodeURIComponent(
                  `${t.quiz.whatsappMsg}\n\nLevel: ${getRecommendation().tier} (${getRecommendation().price})\n\nAnswers:\n1. Experience: ${getAnswerLabels().q1}\n2. Markets: ${getAnswerLabels().q2}\n3. Capital: ${getAnswerLabels().q3}\n4. Time: ${getAnswerLabels().q4}\n5. Psychology: ${getAnswerLabels().q5}\n6. Goal: ${getAnswerLabels().q6}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="quiz-whatsapp-btn"
                data-testid="quiz-whatsapp-btn"
                onClick={() => trackReferralAction("Quiz WhatsApp")}
              >
                <MessageCircle className="w-5 h-5" />
                {t.quiz.contactUs}
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Login Modal */}
      <Dialog open={loginOpen} onOpenChange={(open) => { setLoginOpen(open); if (!open) { setAuthError(""); setAuthSuccess(""); setAuthMode("signin"); } }}>
        <DialogContent className="login-modal" data-testid="login-modal">
          <DialogHeader>
            <DialogTitle className="login-modal-title">{t.loginTitle}</DialogTitle>
          </DialogHeader>
          <div className="login-content">
            <p className="login-subtitle">{t.loginSubtitle}</p>

            {/* Email/Password Form */}
            <form className="auth-email-form" onSubmit={handleEmailAuth} data-testid="email-auth-form">
              <input
                type="email"
                className="auth-input"
                placeholder={t.emailPlaceholder}
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                required
                data-testid="auth-email-input"
              />
              <input
                type="password"
                className="auth-input"
                placeholder={t.passwordPlaceholder}
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                required
                minLength={6}
                data-testid="auth-password-input"
              />
              {authError && <p className="auth-error" data-testid="auth-error">{authError}</p>}
              {authSuccess && <p className="auth-success" data-testid="auth-success">{authSuccess}</p>}
              <button
                type="submit"
                className="auth-submit-btn"
                disabled={authLoading}
                data-testid="auth-submit-btn"
              >
                {authMode === "signin" ? t.signIn : t.signUp}
              </button>
              <p className="auth-toggle">
                {authMode === "signin" ? t.noAccount : t.hasAccount}{" "}
                <button
                  type="button"
                  className="auth-toggle-btn"
                  onClick={() => { setAuthMode(authMode === "signin" ? "signup" : "signin"); setAuthError(""); setAuthSuccess(""); }}
                  data-testid="auth-toggle-btn"
                >
                  {authMode === "signin" ? t.signUp : t.signIn}
                </button>
              </p>
            </form>

            {/* Divider */}
            <div className="auth-divider">
              <span>{t.orContinueWith}</span>
            </div>

            {/* Google Login */}
            <div className="auth-buttons">
              <button
                className="auth-btn google-btn"
                onClick={handleGoogleLogin}
                disabled={authLoading}
                data-testid="google-login-btn"
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t.continueWithGoogle}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
